import { API_BASE_URL } from '../config/api';
import { authService } from './authService';

export type CreatorCatalogKind = 'book' | 'audio' | 'animation';

export interface PublishCreatorCatalogInput {
  kind: CreatorCatalogKind;
  title: string;
  summary: string;
  categoryKey: string;
  language?: string;
  coverFile?: File | null;
  bookFile?: File | null;
  audioFile?: File | null;
  trailerFile?: File | null;
  fullVideoFile?: File | null;
  pictureFile?: File | null;
  publisherName?: string;
  publisherEmail?: string;
  publisherPhone?: string;
  publisherUserId?: string;
}

export interface PublishCreatorCatalogResult {
  success: boolean;
  productId?: string;
  productSlug?: string;
  publishedCompany?: string;
  message?: string;
}

class CreatorCatalogService {
  private baseUrl: string;

  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    const token = authService.getToken();
    const storedUser = authService.getStoredUser();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (storedUser?.id) {
      headers['x-user-id'] = storedUser.id;
    }

    if (storedUser?.role) {
      headers['x-user-role'] = storedUser.role;
    }

    return headers;
  }

  private buildFormData(input: PublishCreatorCatalogInput): FormData {
    const formData = new FormData();
    formData.append('kind', input.kind);
    formData.append('name', input.title.trim());
    formData.append('titleKey', input.title.trim());
    formData.append('overview', input.summary.trim());
    formData.append('descriptionKey', input.summary.trim());
    formData.append('categoryKey', input.categoryKey.trim() || 'uncategorized');
    formData.append('audience', 'public');
    formData.append('currency', 'RWF');
    formData.append('cost', '0');
    formData.append('price', '0');
    formData.append('language', (input.language?.trim() || 'en').toLowerCase());
    formData.append('publishedCompany', input.publisherName?.trim() || 'Haapa Publisher');
    formData.append('author', input.publisherName?.trim() || 'Haapa Publisher');
    formData.append('uploaderName', input.publisherName?.trim() || 'Haapa Publisher');

    if (input.publisherUserId?.trim()) {
      formData.append('uploaderId', input.publisherUserId.trim());
      formData.append('userId', input.publisherUserId.trim());
      formData.append('ownerId', input.publisherUserId.trim());
    }

    if (input.publisherEmail?.trim()) {
      formData.append('publisherEmail', input.publisherEmail.trim());
      formData.append('uploaderEmail', input.publisherEmail.trim());
    }

    if (input.publisherPhone?.trim()) {
      formData.append('publisherPhone', input.publisherPhone.trim());
      formData.append('publisherAltPhone', input.publisherPhone.trim());
    }

    if (input.kind === 'book') {
      formData.append('bookFormat', 'digital');
      if (input.bookFile) {
        formData.append('bookFile', input.bookFile);
      }
      if (input.coverFile) {
        formData.append('coverFile', input.coverFile);
      }
    }

    if (input.kind === 'audio') {
      if (input.audioFile) {
        formData.append('audioFile', input.audioFile);
      }
      if (input.coverFile) {
        formData.append('coverFile', input.coverFile);
      }
    }

    if (input.kind === 'animation') {
      if (input.trailerFile) {
        formData.append('trailerFile', input.trailerFile);
      }
      if (input.fullVideoFile) {
        formData.append('fullVideoFile', input.fullVideoFile);
      }
      if (input.pictureFile) {
        formData.append('pictureFile', input.pictureFile);
        formData.append('coverFile', input.pictureFile);
      }
    }

    return formData;
  }

  async publish(input: PublishCreatorCatalogInput): Promise<PublishCreatorCatalogResult> {
    const paths = ['/products', '/creators/catalog-items'];
    let lastError: Error | null = null;

    for (let index = 0; index < paths.length; index += 1) {
      const path = paths[index];
      const hasNextPath = index < paths.length - 1;

      try {
        const response = await fetch(`${this.baseUrl}${path}`, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: this.buildFormData(input),
        });

        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        if ((response.status === 404 || response.status === 405) && hasNextPath) {
          continue;
        }

        if (!response.ok) {
          const payloadRecord = this.asRecord(payload);
          const dataRecord = this.asRecord(payloadRecord.data);
          const message =
            this.asString(payloadRecord.message) ||
            this.asString(payloadRecord.error) ||
            this.asString(dataRecord.message) ||
            this.asString(dataRecord.error) ||
            `Request failed with status ${response.status}`;
          throw new Error(message);
        }

        const payloadRecord = this.asRecord(payload);
        const dataRecord = this.asRecord(payloadRecord.data);
        const productRecord = this.asRecord(dataRecord.product);

        return {
          success: true,
          productId: this.asString(productRecord.id),
          productSlug: this.asString(productRecord.slug),
          publishedCompany:
            this.asString(productRecord.publishedCompany) ||
            input.publisherName?.trim(),
          message:
            this.asString(payloadRecord.message) ||
            this.asString(dataRecord.message) ||
            'Content published successfully.',
        };
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unable to publish content.');
      }
    }

    throw lastError ?? new Error('Unable to publish content.');
  }
}

export const creatorCatalogService = new CreatorCatalogService();
